import React from 'react';
import { CustomerByODService, FluxClientOD } from 'types';
import styled from 'styled-components';
import { Tooltip } from 'antd';
import { TooltipPlacement } from 'antd/lib/tooltip';

import { BASIC_ROW_HEIGHT, Basic, getTooltipTitle, isValueStringEmpty } from './utils';

type LeftTooltipKey = 'departureStationFullName';
type RightTooltipKey =
  | 'firstTrainAssistanceUp'
  | 'secondTrainAssistanceUp'
  | 'firstTrainUfrUp'
  | 'secondTrainUfrUp'
  | 'firstTrainChildUp'
  | 'secondTrainChildUp';
interface Props {
  customerService: CustomerByODService;
  fluxClientODKey: keyof Omit<FluxClientOD, 'fluxClientODDown'>;
  formatDisplay?: (value: string | number | boolean | null | undefined) => string;
  tooltipPlacement?: TooltipPlacement;
  tooltipTitle?: string;
  leftTooltipKey?: LeftTooltipKey;
  rightTooltipKeys?: Array<RightTooltipKey>;
  forceRightPlacement?: boolean;
}

type StyledFluxClientDisplayItemProps = {
  empty: boolean;
  hasException?: boolean;
  height?: number;
};

const StyledFluxClientDisplayItem = styled.div<StyledFluxClientDisplayItemProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  ${(props) => props.hasException && 'color: red'};
  ${(props) => props?.height && `height: ${props.height}px`};
  padding-top: 5px;
  padding-bottom: 5px;
  &:not(:last-child) {
    border-bottom: 2px solid #d8d8d8;
  }
  &:after {
    content: ${(props) => (props.empty ? '"\xA0"' : '""')};
  }
`;

const FluxClientUpDisplay: React.FC<Props> = ({
  tooltipPlacement = 'left',
  tooltipTitle = '',
  customerService,
  fluxClientODKey,
  formatDisplay,
  leftTooltipKey,
  rightTooltipKeys,
  forceRightPlacement,
}) => {
  const HasTooltipTitleProp = ({ value, title }: { value: Basic; title: string }) => (
    <Tooltip title={title} placement={tooltipPlacement}>
      {formatDisplay ? formatDisplay(value) : value}
    </Tooltip>
  );

  const HasOnlyLeftTooltipKey = ({ value, title }: { value: Basic; title: string }) => (
    <Tooltip title={title} placement={forceRightPlacement ? 'right' : 'left'}>
      {formatDisplay ? formatDisplay(value) : value}
    </Tooltip>
  );

  const HasOnlyRightTooltipKeys = ({ value, title }: { value: Basic; title: string }) => (
    <Tooltip title={title} placement="right">
      {formatDisplay ? formatDisplay(value) : value}
    </Tooltip>
  );

  const HasOnlyBothLeftAndRightTooltipKeys = ({
    value,
    titleLeft,
    titleRight,
  }: {
    value: Basic;
    titleLeft: string;
    titleRight: string;
  }) => (
    <Tooltip title={titleRight} placement="right">
      <Tooltip title={titleLeft} placement="left">
        {formatDisplay ? formatDisplay(value) : value}
      </Tooltip>
    </Tooltip>
  );

  return (
    <div>
      {customerService?.fluxClientODs &&
        customerService?.fluxClientODs.map((fluxClientItem) => (
          <StyledFluxClientDisplayItem
            height={fluxClientItem.fluxClientODDown.length * BASIC_ROW_HEIGHT}
            key={`${fluxClientODKey}_${fluxClientItem.departureStationUic}`}
            empty={isValueStringEmpty(fluxClientItem[fluxClientODKey])}
            hasException={customerService.isCancel || fluxClientItem.isDepartureStationStopSkip}
          >
            {!rightTooltipKeys && !leftTooltipKey && !!tooltipTitle && (
              <HasTooltipTitleProp value={fluxClientItem[fluxClientODKey]} title={tooltipTitle} />
            )}

            {!tooltipTitle && !rightTooltipKeys && !!leftTooltipKey && (
              <HasOnlyLeftTooltipKey
                value={fluxClientItem[fluxClientODKey]}
                title={getTooltipTitle({ value1: fluxClientItem[leftTooltipKey] })}
              />
            )}

            {!tooltipTitle && !leftTooltipKey && !!rightTooltipKeys && (
              <HasOnlyRightTooltipKeys
                value={fluxClientItem[fluxClientODKey]}
                title={
                  rightTooltipKeys.length === 1
                    ? getTooltipTitle({ value1: fluxClientItem[rightTooltipKeys[0]] })
                    : getTooltipTitle({
                        value1: fluxClientItem[rightTooltipKeys[0]],
                        value2: fluxClientItem[rightTooltipKeys[1]],
                      })
                }
              />
            )}

            {!tooltipTitle && !!rightTooltipKeys && !!leftTooltipKey && (
              <HasOnlyBothLeftAndRightTooltipKeys
                value={fluxClientItem[fluxClientODKey]}
                titleLeft={getTooltipTitle({ value1: fluxClientItem[leftTooltipKey] })}
                titleRight={
                  rightTooltipKeys.length === 1
                    ? getTooltipTitle({ value1: fluxClientItem[rightTooltipKeys[0]] })
                    : getTooltipTitle({
                        value1: fluxClientItem[rightTooltipKeys[0]],
                        value2: fluxClientItem[rightTooltipKeys[1]],
                      })
                }
              />
            )}

            {!tooltipTitle && !rightTooltipKeys && !leftTooltipKey && (
              <>{formatDisplay ? formatDisplay(fluxClientItem[fluxClientODKey]) : fluxClientItem[fluxClientODKey]}</>
            )}
          </StyledFluxClientDisplayItem>
        ))}
    </div>
  );
};

export default FluxClientUpDisplay;
